import React, { useMemo, useCallback } from 'react';
import FieldVisibilityDropdown from './FieldVisibilityDropdown';
import SwimlanesDropdown from './SwimlanesDropdown';
import SortDropdown from './SortDropdown';
import KanbanFilterDropdown from './KanbanFilterDropdown/index';
import ColumnLimitsDropdown from './ColumnLimitsDropdown';
import FokusButton from '../shared/FokusButton';
import { calculateLockStates } from '../FlowchartView/utils/dependencyUtils';
import { isTaskActionable } from '../shared/utils/taskAvailabilityUtils';
import {
  ToolbarArchiveToggle,
  ColumnsIcon,
  MissionIcon,
  DiamondIcon,
  HourglassIcon,
  SearchIcon,
  ResponsiveToolbar,
} from '../shared/ToolbarPrimitives';
import ResetCampaignToolbarItem from './ResetCampaignToolbarItem';
import VariablesToolbarItem from './VariablesToolbarItem';

/**
 * KanbanToolbar Component
 * 
 * Toolbar for Kanban view controls.
 * - Add Mission/Decision buttons to create new tasks
 * - Add Column button to create new status columns
 * - Undo/Redo buttons (shared history with other views)
 */
const KanbanToolbar = ({ 
  onAddTask,
  onAddDecision,
  onAddWait,
  onAddColumn,
  cardDisplaySettings,
  onCardDisplaySettingsChange,
  // Custom fields (campaign-level definitions)
  customFields = [],
  onManageCustomFields,
  swimlaneSetting,
  onSwimlaneSettingChange,
  swimlaneContext,
  sortSetting,
  onSortSettingChange,
  // Filter props
  filters,
  onFiltersChange,
  statuses,
  categories,
  allTags,
  tasks,
  // WIP limits
  onSetWipLimit,
  // Archive props
  showArchived = false,
  onToggleArchived,
  archivedCount = 0,
  // Actionable only filter
  showActionableOnly = false,
  onToggleActionableOnly,
  // Nested campaigns visibility filter
  showNestedCampaigns = true,
  onShowNestedCampaignsChange,
  // Nested sprints for mission availability calculation
  nestedSprints = [],
  // FOKUS mode props
  isFokusMode = false,
  selectedTaskIds = new Set(),
  onFokusSelected,
  onExitFokusMode,
  onFokusByFilter,
  // Mission search
  onOpenMissionSearch,
  onResetCampaignProgress,
  onManageVariables = null,
}) => {
  // Calculate lock states for mission availability filtering
  const lockStates = useMemo(() => {
    return calculateLockStates(tasks, nestedSprints, statuses);
  }, [tasks, nestedSprints, statuses]);

  // Calculate actionable task IDs for FOKUS mode
  const actionableTaskIds = useMemo(() => {
    return tasks
      .filter(task => isTaskActionable(task, lockStates, nestedSprints, statuses))
      .map(task => task.id);
  }, [tasks, lockStates, nestedSprints, statuses]);

  // Handle adding mission and scrolling to it
  const handleAddMission = useCallback(() => {
    const taskId = onAddTask?.();
    if (taskId) {
      // Dispatch focusMission event to scroll to the new card
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('focusMission', {
          detail: { missionId: taskId, view: 'kanban' }
        }));
      }, 100);
    }
  }, [onAddTask]);

  // Handle adding decision and scrolling to it
  const handleAddDecision = useCallback(() => {
    const taskId = onAddDecision?.();
    if (taskId) {
      // Dispatch focusMission event to scroll to the new card
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('focusMission', {
          detail: { missionId: taskId, view: 'kanban' }
        }));
      }, 100);
    }
  }, [onAddDecision]);

  const handleAddWait = useCallback(() => {
    const taskId = onAddWait?.();
    if (taskId) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('focusMission', {
          detail: { missionId: taskId, view: 'kanban' }
        }));
      }, 100);
    }
  }, [onAddWait]);

  return (
    <ResponsiveToolbar>
      {/* ═══════════════════════════════════════════════════════════════
          SHARED BY ALL 5 VIEWS (same order everywhere)
          Priority 1-3 = most important, hidden last
          ═══════════════════════════════════════════════════════════════ */}
      
      {/* 1-2. Add Mission/Decision (ALL views) - Priority 1 (always visible) */}
      <ResponsiveToolbar.Item priority={1}>
        <div className="unified-toolbar__group">
          <button
            className="toolbar-btn toolbar-btn--accent"
            onClick={handleAddMission}
            title="Add new mission"
          >
            <span className="toolbar-btn__content">
              <span className="toolbar-btn__icon">
                <MissionIcon />
              </span>
              <span className="toolbar-btn__label">Add Mission</span>
            </span>
          </button>
          
          <button
            className="toolbar-btn"
            onClick={handleAddDecision}
            title="Add decision node"
          >
            <span className="toolbar-btn__content">
              <span className="toolbar-btn__icon">
                <DiamondIcon />
              </span>
              <span className="toolbar-btn__label">Add Decision</span>
            </span>
          </button>
          <button
            className="toolbar-btn"
            onClick={handleAddWait}
            title="Add a wait-for card"
          >
            <span className="toolbar-btn__content">
              <span className="toolbar-btn__icon">
                <HourglassIcon />
              </span>
              <span className="toolbar-btn__label">Add Wait for</span>
            </span>
          </button>
        </div>
      </ResponsiveToolbar.Item>

      {/* 3. FOKUS Button (ALL views) - Priority 2 */}
      <ResponsiveToolbar.Item priority={2}>
        <FokusButton
          isFocusMode={isFokusMode}
          hasSelection={selectedTaskIds.size > 0}
          selectedCount={selectedTaskIds.size}
          actionableTaskIds={actionableTaskIds}
          onFocusSelected={(ids) => onFokusSelected?.(ids)}
          onExitFocusMode={onExitFokusMode}
          onFilterFocus={onFokusByFilter}
          tasks={tasks}
          categories={categories}
          statuses={statuses}
          allTags={allTags}
          lockStates={lockStates}
          nestedSprints={nestedSprints}
          selectionLabel="Selected Cards"
        />
      </ResponsiveToolbar.Item>

      {/* 4. Filter (ALL views) - Priority 3 */}
      <ResponsiveToolbar.Item priority={3}>
        <KanbanFilterDropdown
          filters={filters}
          onFiltersChange={onFiltersChange}
          statuses={statuses}
          categories={categories}
          allTags={allTags}
          tasks={tasks}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          lockStates={lockStates}
          nestedSprints={nestedSprints}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          customFields={customFields}
        />
      </ResponsiveToolbar.Item>

      {/* 5. WIP Limits (ALL views) - Priority 5 */}
      <ResponsiveToolbar.Item priority={5}>
        <ColumnLimitsDropdown
          statuses={statuses}
          tasks={tasks}
          onSetWipLimit={onSetWipLimit}
        />
      </ResponsiveToolbar.Item>

      {/* ═══════════════════════════════════════════════════════════════
          SHARED BY 4 VIEWS (not Flow)
          ═══════════════════════════════════════════════════════════════ */}

      {/* 6. Sort (Kanban, Table, Gantt, Todo) - Priority 6 */}
      <ResponsiveToolbar.Item priority={6}>
        <SortDropdown
          currentSort={sortSetting}
          onSortChange={onSortSettingChange}
          customFields={customFields}
        />
      </ResponsiveToolbar.Item>

      {/* 7. Archive Toggle (Kanban, Table, Gantt, Todo) - Priority 8 */}
      <ResponsiveToolbar.Item priority={8}>
        <ToolbarArchiveToggle
          showArchived={showArchived}
          onToggleArchived={onToggleArchived}
          archivedCount={archivedCount}
        />
      </ResponsiveToolbar.Item>

      {/* ═══════════════════════════════════════════════════════════════
          SHARED BY 3 VIEWS
          ═══════════════════════════════════════════════════════════════ */}

      {/* 8. Swimlanes (Kanban, Gantt, Todo) - Priority 7 */}
      <ResponsiveToolbar.Item priority={7}>
        <SwimlanesDropdown
          currentSwimlane={swimlaneSetting}
          onSwimlaneChange={onSwimlaneSettingChange}
          context={swimlaneContext}
        />
      </ResponsiveToolbar.Item>

      {/* ═══════════════════════════════════════════════════════════════
          SHARED BY 4 VIEWS (Flow, Kanban, Table, Todo - not Gantt)
          ═══════════════════════════════════════════════════════════════ */}

      {/* 9. Field Visibility (Flow, Kanban, Table, Todo) - Priority 9 */}
      <ResponsiveToolbar.Item priority={9}>
        <FieldVisibilityDropdown
          view="kanban"
          visibility={cardDisplaySettings}
          onVisibilityChange={onCardDisplaySettingsChange}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
        />
      </ResponsiveToolbar.Item>

      {/* ═══════════════════════════════════════════════════════════════
          VIEW-SPECIFIC (Kanban only)
          ═══════════════════════════════════════════════════════════════ */}

      {/* Kanban-specific: Add Column - Priority 10 (hidden first) */}
      <ResponsiveToolbar.Item priority={10}>
        <button 
          className="toolbar-btn"
          onClick={onAddColumn}
          title="Add new column (status)"
        >
          <span className="toolbar-btn__content">
            <span className="toolbar-btn__icon">
              <ColumnsIcon />
            </span>
            <span className="toolbar-btn__label">Add Column</span>
          </span>
        </button>
      </ResponsiveToolbar.Item>

      {onResetCampaignProgress && (
        <ResponsiveToolbar.Item priority={11} label="Reset campaign">
          <ResetCampaignToolbarItem onResetCampaignProgress={onResetCampaignProgress} />
        </ResponsiveToolbar.Item>
      )}
      {onManageVariables && (
        <ResponsiveToolbar.Item priority={12} label="Variables">
          <VariablesToolbarItem onManageVariables={onManageVariables} />
        </ResponsiveToolbar.Item>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SEARCH (ALL views - always at end)
          ═══════════════════════════════════════════════════════════════ */}
      
      {/* Mission Search - Priority 4 (important, stays visible) */}
      <ResponsiveToolbar.Item priority={4}>
        <button
          className="toolbar-btn"
          onClick={onOpenMissionSearch}
          title="Search missions (Ctrl+F)"
        >
          <span className="toolbar-btn__content">
            <span className="toolbar-btn__icon">
              <SearchIcon />
            </span>
            <span className="toolbar-btn__label">Search</span>
          </span>
        </button>
      </ResponsiveToolbar.Item>
    </ResponsiveToolbar>
  );
};

export default KanbanToolbar;
